/**
 * 出貨單建立 / 編輯表單(REQ-20260806002;編輯模式 REQ-20260806005)。
 *
 * 三件需求直接對應到這個畫面:
 *  1. 打客戶編號 → PartyPicker 帶出統編 / 送貨地址 / 電話
 *  2. 「導入訪談紀錄」→ 把訪談的規格文字一鍵填進品名 / 規格
 *  3. 才數 / 模數 + 版費 / 刀模費,金額即時試算
 *
 * 金額只是**試算**給人看,送出後由後端用 Decimal 重算 —— 兩邊公式共用
 * `@app/shared` 的 `calcOrderTotals()` + `calcTaxModeAmounts()`(REQ-20260903008
 * 起合計區也依稅別拆稅),不會各算各的。稅別本身自 REQ-20260921002 起是合計區旁邊
 * 那顆下拉,建單與編輯都改得動。
 *
 * 帶 `editOrder` 時進入編輯模式:後端放行 DRAFT 與尚未沖帳的 POSTED
 * (REQ-20260807001),客戶不可換 —— `UpdateSalesOrderSchema` 本來就沒有 `customerId`。
 *
 * 欄位與訪談頁「新增應收單」對齊(REQ-20260811003):貨品類別、帳款抬頭 / 對象、
 * 印刷顏色 / 稿件處理 / 出貨方式 / 印刷面別。那邊填的東西這裡本來看不到也改不到,
 * 而編輯會整批重建明細 —— 看不到就等於存一次檔就被清掉。
 *
 * 編輯**草稿**時底部多一顆「刪除」(REQ-20260812001)。規則與清單頁完全同一條:
 * 只有 DRAFT 可刪,已過帳的只給作廢 —— 單號是連號、沖帳靠 `sourceDocNo` 字串對應
 * (REQ-20260806005 §二)。這裡只是多開一個入口,沒有放寬規則。
 *
 * 每個品項可以選「填單價」或「填總價」(REQ-20260905007),與訪談「新增應收單」
 * 同一組切換。填總價時**總價才是金額的權威來源**,單價由後端回推成參考值 ——
 * 前端不做這個除法(REQ-20260807002 §二:1000 ÷ 3 存 333.33 再乘回去只有 999.99)。
 *
 * 帶 `readOnly` 時是**唯讀檢視**(REQ-20260824009):已沖帳的單不能改、但要能查閱。
 * 沿用同一份版面(不另外複製一份唯讀版),整張表單包在 `fieldset[disabled]` 裡,
 * 只把會寫入的按鈕收起來 —— 詳見下面 `<fieldset disabled={readOnly}>` 那段註解。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CreateSalesOrderSchema,
  UpdateSalesOrderSchema,
  calcLineAmount,
  calcOrderTotals,
  calcTaxModeAmounts,
  type CreateSalesOrderInput,
  type PartyInfo,
  type SalesOrderDetail,
} from '@app/shared';
import { getApiErrorMessage } from '../../lib/api-client';
import { formatMoney } from '../../lib/format-money';
import { describeZodError, type FieldLabelMap } from '../../lib/zod-form-error';
import { AddressInput, splitAddressZip } from '../../components/AddressInput';
import { ArtworkPicker, artworkImages } from '../../components/ArtworkPicker';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  MultiSelectField,
  joinItems,
  optionNames,
  splitItems,
} from '../../components/MultiSelectField';
import { PartyPicker } from '../../components/PartyPicker';
import { OpenRemindersBanner } from '../party-reminders/OpenRemindersBanner';
import {
  usePayerNames,
  useCreateSalesOrder,
  useDeleteSalesOrder,
  useNextSalesOrderNo,
  useSalesPersons,
  useUpdateSalesOrder,
} from '../accounting/api';
import { useInterview } from '../interviews/api';
import {
  PERSONAL_CERT_KEYWORDS,
  PERSONAL_METHOD_KEYWORDS,
  matchReferenceId,
} from '../accounting/payer-defaults';
import { useReferences } from '../settings/api';
import { InterviewImportPicker } from './InterviewImportPicker';

interface LineDraft {
  productName: string;
  productSpec: string;
  productMaterial: string;
  /** 設定頁「貨品類別」的 id。存的是 id(FK),與其他文字欄位不同 */
  productCategoryId: number | null;
  productProcessing: string;
  unitName: string;
  qty: string;
  /**
   * 這一列填的是單價還是整批總價(REQ-20260905007)。只影響輸入方式與送出哪一格,
   * 不是單據欄位 —— DB 存的仍然是 `unitPrice` + `amount`。
   *
   * 品項是本地 `useState` 而不是 react-hook-form,所以不會踩到 REQ-20260807002
   * 在「新增應收單」那邊記下的兩個坑(RHF 的 radio 一律回字串、沒 render 的欄位
   * 會從殘留 DOM 讀回舊值),不需要 `register()` 技巧。
   */
  priceMode: 'unit' | 'total';
  unitPrice: string;
  totalPrice: string;
  sizeUnits: string;
  moldCount: string;
  /** 編輯既有單時保留原本的貨品連結;手打的新列一律 MANUAL / 無 productId */
  productId: number | null;
  productCode: string;
}

const EMPTY_LINE: LineDraft = {
  productName: '',
  productSpec: '',
  productMaterial: '',
  productCategoryId: null,
  productProcessing: '',
  unitName: '',
  qty: '1',
  priceMode: 'unit',
  unitPrice: '0',
  totalPrice: '',
  sizeUnits: '',
  moldCount: '',
  productId: null,
  productCode: 'MANUAL',
};

/**
 * 品項 → 計價用的輸入。小計預覽與合計區都走這支,**模式的真相只有一格** ——
 * 單價模式一律把 `totalPrice` 送成 null(而不是留著另一格的殘值),
 * 否則畫面上顯示的是單價、算的卻是上一次填的總價。
 * 送 `null` 而不是 `undefined`:`exactOptionalPropertyTypes` 不收顯式的 undefined,
 * 而 `calcLineAmount()` 對兩者一樣是「沒填總價」。
 */
function linePricing(line: LineDraft): {
  qty: string;
  unitPrice: string;
  totalPrice: string | null;
  sizeUnits: string;
  moldCount: string;
} {
  return {
    qty: line.qty,
    unitPrice: line.unitPrice,
    totalPrice: line.priceMode === 'total' ? line.totalPrice : null,
    sizeUnits: line.sizeUnits,
    moldCount: line.moldCount,
  };
}

/**
 * 合計區「稅額」後面的稅別註記(REQ-20260903008)。措辭沿用列印頁「課稅類別」那格
 * (外加 5% / 內含 / 免稅),但**不共用常數** —— 那邊是單據上的正式欄位,這邊只是
 * 解釋稅額為什麼是 0(或為什麼未稅比品項金額少)。外加是預設,不加註記,
 * 建單畫面與改動前逐字相同。
 */
const TAX_MODE_NOTE: Record<SalesOrderDetail['taxMode'], string> = {
  EXCLUSIVE: '',
  INCLUSIVE: '(內含)',
  EXEMPT: '(免稅)',
};

/**
 * 送出前的 zod 驗證失敗時,拿來把欄位路徑翻成畫面上的中文標籤(REQ-20260818003)。
 * 後端驗證擋下、回應帶 `error.fields` 時也用同一份(REQ-20260929001)。
 * 值就是各欄位 JSX 上的 label 文字 —— 使用者是照著畫面找那一格的。
 */
const FIELD_LABELS: FieldLabelMap = {
  customerId: '客戶',
  txnDate: '訂單日期',
  salesPersonId: '業務員',
  shippingAddress: '送貨地址',
  payerName: '實際收款對象',
  paymentCertId: '收執憑證',
  paymentMethodId: '收款方式',
  printColor: '印刷顏色',
  artworkHandling: '稿件處理',
  printSides: '印刷面別',
  shippingMethod: '出貨方式',
  plateFee: '版費',
  dieFee: '刀模費',
  notes: '備註',
  lines: '品項',
  'lines.#.productName': '品名',
  'lines.#.productCategoryId': '貨品類別',
  'lines.#.productSpec': '規格',
  'lines.#.productMaterial': '材質',
  'lines.#.productProcessing': '加工',
  'lines.#.unitName': '單位',
  'lines.#.qty': '數量',
  'lines.#.unitPrice': '單價',
  'lines.#.totalPrice': '總價',
  'lines.#.sizeUnits': '才數',
  'lines.#.moldCount': '模數',
};

interface Props {
  /**
   * 建立 / 儲存成功。**帶 id 而不只是單號**(REQ-20260811004)——
   * 呼叫端要靠它組出 `/sales-orders/:id/print` 的連結,與訪談「新增應收單」
   * 成功後那個「查看 / 列印藝品印刷訂單」是同一個互動。
   */
  onCreated: (order: { id: number; orderNo: string }) => void;
  onCancel: () => void;
  /** 有值即編輯模式;沒有就是建新單 */
  editOrder?: SalesOrderDetail;
  /** 草稿刪除成功(REQ-20260812001)。呼叫端負責收掉表單並提示 */
  onDeleted?: () => void;
  /**
   * 唯讀檢視(REQ-20260824009)。已沖帳的單不能改、但要能**查閱** ——
   * 沿用同一份版面,只是整張表單包進 `fieldset[disabled]`、收掉會寫入的按鈕。
   * 不另外複製一份唯讀版面:欄位以後增修才不會有兩份要一起改。
   */
  readOnly?: boolean;
}

export function SalesOrderForm({
  onCreated,
  onCancel,
  editOrder,
  onDeleted,
  readOnly = false,
}: Props): JSX.Element {
  const isEdit = editOrder != null;
  const [customer, setCustomer] = useState<PartyInfo | null>(
    editOrder ? partyFromOrder(editOrder) : null,
  );
  const [interviewId, setInterviewId] = useState<number | null>(editOrder?.interviewId ?? null);
  const [txnDate, setTxnDate] = useState(editOrder?.txnDate ?? todayInTaipei());
  /**
   * 業務員(REQ-20260815002)。`sales_orders.salesPersonId` 早就存在,但在此之前
   * 沒有任何畫面寫得進去 —— 歷史單一律 null,選單裡對應「未指定」那一項。
   */
  const [salesPersonId, setSalesPersonId] = useState<number | null>(
    editOrder?.salesPersonId ?? null,
  );
  const [shippingAddress, setShippingAddress] = useState(editOrder?.shippingAddress ?? '');
  const [plateFee, setPlateFee] = useState(editOrder?.plateFee ?? '');
  const [dieFee, setDieFee] = useState(editOrder?.dieFee ?? '');
  /**
   * 單頭稅別(REQ-20260921002)。使用者回報「修改訂單時找不到可以修改稅額的地方」——
   * 稅額是稅別算出來的,而這張表單從來沒有稅別欄位(REQ-20260903008 §5.1)。
   * 建單與編輯共用這一格:建單預設外加 5%(= 後端 zod 的預設值),
   * 編輯帶出單據原本的稅別。下拉擺在合計區旁邊,理由同進貨單(REQ-20260901010)。
   */
  const [taxMode, setTaxMode] = useState<SalesOrderDetail['taxMode']>(
    editOrder?.taxMode ?? 'EXCLUSIVE',
  );
  const [notes, setNotes] = useState(editOrder?.notes ?? '');
  const [lines, setLines] = useState<LineDraft[]>(
    editOrder ? editOrder.lines.map(toLineDraft) : [{ ...EMPTY_LINE }],
  );
  const [formError, setFormError] = useState<string | null>(null);

  /**
   * 帳款抬頭 / 對象(REQ-20260810002)。與 `CreateReceivableModal` 同一組欄位、
   * 同一組切換規則,只是那邊是建單、這邊還要能帶出既有單的原值。
   */
  const [payerType, setPayerType] = useState<'COMPANY' | 'PERSONAL'>(
    editOrder?.payerType === 'PERSONAL' ? 'PERSONAL' : 'COMPANY',
  );
  const [payerName, setPayerName] = useState(editOrder?.payerName ?? '');
  const [paymentCertId, setPaymentCertId] = useState<number | null>(
    editOrder?.paymentCertId ?? null,
  );
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(
    editOrder?.paymentMethodId ?? null,
  );

  /**
   * 藝品印刷訂單的四個現場欄位(REQ-20260809002、REQ-20260810003)。
   * 印刷顏色 / 稿件處理可複選,存成「、」串接的字串 —— 編輯時用 `splitItems()` 拆回來,
   * 對不上選項清單的舊值會當成手動輸入的項目照樣顯示,不會憑空消失。
   */
  const [printColorItems, setPrintColorItems] = useState<string[]>(
    splitItems(editOrder?.printColor),
  );
  const [artworkHandlingItems, setArtworkHandlingItems] = useState<string[]>(
    splitItems(editOrder?.artworkHandling),
  );
  const [shippingMethod, setShippingMethod] = useState(editOrder?.shippingMethod ?? '');
  const [printSides, setPrintSides] = useState(editOrder?.printSides ?? '');

  /**
   * 商品稿樣(REQ-20260811004)。候選圖片是**目前關聯的那筆訪談**的圖片附件 ——
   * 出貨管理的單不一定有來源訪談,所以要先有 `interviewId` 才撈得到候選清單
   * (這正是 REQ-20260809004 / REQ-20260811003 當時把稿樣排除在外的那個未解問題,
   * 而 `InterviewImportPicker` 已經讓使用者隨時能關聯 / 更換訪談)。
   * 同樣用本地 state 而不是 `register()`:存的是 id 陣列,不是單一 input 的字串值。
   */
  const [artworkAttachmentIds, setArtworkAttachmentIds] = useState<number[]>([]);
  /** 送出被攔下時開的確認視窗(REQ-20260903009);詳見下面 `artworkEmpty` 那段 */
  const [artworkConfirmOpen, setArtworkConfirmOpen] = useState(false);
  /** 「返回勾選」要把畫面捲回稿樣區並聚焦第一格 */
  const artworkSectionRef = useRef<HTMLDivElement>(null);
  const { data: interview } = useInterview(interviewId);

  /**
   * 「這張表單有沒有被改過」(REQ-20260917002)。
   *
   * 這裡**自己算一份**:這張表單不是 react-hook-form(每一格都是獨立的 `useState`),
   * 沒有 `formState.isDirty` 可用,也沒有像客戶 / 廠商操作台那樣的外部 `dirty` state
   * (那兩條分別是 REQ-20260825009 與 REQ-20260819011 的做法)。做法是把**送出時會讀到
   * 的那些 state** 序列化,跟掛載當下的那一份比字串 —— 兩份由同一段程式產生,
   * key 順序必然相同。
   *
   * 客戶**只取 id**:payload 認的就是 id(送貨地址留白時才拿主檔地址當 fallback,
   * 那不是使用者改得動的東西),而整個 `PartyInfo` 物件比字串容易受欄位順序影響。
   * 純畫面狀態(錯誤訊息、稿樣確認視窗、刪除確認列)不列入。
   * 唯讀模式整張表包在 `fieldset[disabled]` 裡,改不動任何一格 → 恆非 dirty。
   */
  const snapshot = useMemo(
    () =>
      JSON.stringify({
        customerId: customer?.id ?? null,
        interviewId,
        salesPersonId,
        txnDate,
        shippingAddress,
        payerType,
        payerName,
        paymentCertId,
        paymentMethodId,
        printColorItems,
        artworkHandlingItems,
        shippingMethod,
        printSides,
        artworkAttachmentIds,
        plateFee,
        dieFee,
        taxMode,
        notes,
        lines,
      }),
    [
      customer,
      interviewId,
      salesPersonId,
      txnDate,
      shippingAddress,
      payerType,
      payerName,
      paymentCertId,
      paymentMethodId,
      printColorItems,
      artworkHandlingItems,
      shippingMethod,
      printSides,
      artworkAttachmentIds,
      plateFee,
      dieFee,
      taxMode,
      notes,
      lines,
    ],
  );
  /**
   * 掛載當下那一份,只取第一次 —— 但稿樣的初始勾選是**非同步回填**的
   * (下面那支 effect 要等訪談回來才對得出 id),所以那一支回填完會把這格清掉重取,
   * 否則單純打開一張有稿樣的單就會自己變成「已修改」。
   * 存檔成功後父層會收掉表單、元件卸載,所以不需要另外重設。
   */
  const initialSnapshot = useRef<string | null>(null);
  if (initialSnapshot.current == null) initialSnapshot.current = snapshot;
  const dirty = initialSnapshot.current !== snapshot;

  /**
   * 忘記按儲存就關分頁 / 重新整理時提醒(REQ-20260917002 需求第 2 點)。
   * 提示文字由瀏覽器決定、不可自訂(規範限制,見 REQ-20260804002)。
   *
   * **只做 `beforeunload`,不做 `useBlocker`** —— 那支需要 data router,而這張表單的
   * 測試是直接 `render(<SalesOrderForm ... />)`、外面連 router 都沒有,掛上去會讓
   * 整個測試檔一起 throw。站內換頁的攔截這次不做。
   */
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  /**
   * 有未儲存變更才問一次(REQ-20260917002)。沒改過就直接關掉,問了只是多按一次。
   * 文案與 `ConfirmDialog` 的用法沿用 REQ-20260916004 的客戶 / 廠商操作台。
   */
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const handleCancel = (): void => {
    if (!dirty) {
      onCancel();
      return;
    }
    setCancelConfirm(true);
  };

  /**
   * 編輯模式的初始勾選:單據上留的是附件 **uuid**(`editOrder.artworks`),
   * 而送出時要的是附件 **id** —— 拿訪談的附件清單把 uuid 對回 id,順序照單據上的順序
   * (= 當初的勾選順序 = 列印排列順序)。對不到的(附件已被刪)就跳過,
   * 與後端 `getOne()` 逐張過濾已刪附件的行為一致。
   *
   * 只在訪談資料第一次到位時套用一次:refetch 後再套一次會把使用者剛改好的勾選蓋掉。
   */
  const artworkInitApplied = useRef(false);
  useEffect(() => {
    if (artworkInitApplied.current || editOrder == null || interview == null) return;
    artworkInitApplied.current = true;
    const idByUuid = new Map(interview.attachments.map((a) => [a.uuid, a.id]));
    setArtworkAttachmentIds(
      editOrder.artworks.map((a) => idByUuid.get(a.uuid)).filter((id): id is number => id != null),
    );
    // 回填的是**單據原本的值**,不是使用者改的 —— 把 dirty 的基準線清掉重取,
    // 否則打開一張有稿樣的單就會顯示「未儲存」(REQ-20260917002)
    initialSnapshot.current = null;
  }, [editOrder, interview]);

  /**
   * 關聯 / 更換 / 取消關聯訪談。**換訪談一定要清掉稿樣的勾選** ——
   * 候選圖片來自訪談,換一筆之後舊的勾選就不成立了(後端也會擋:不屬於這筆訪談的圖
   * 一律 400),留著只會讓使用者以為還勾著上一筆訪談的圖。
   */
  const changeInterview = (next: number | null): void => {
    setInterviewId(next);
    // 使用者自己動過關聯訪談之後,就不該再把單據原本的稿樣套回來
    artworkInitApplied.current = true;
    setArtworkAttachmentIds([]);
  };

  /**
   * 有圖可挑卻一張都沒勾的提醒(REQ-20260903005;REQ-20260903009 改成送出時才問)。
   *
   * 起因與 REQ-20260811004 同一件事:key 表單時漏了帶入圖片選擇,列印訂單的稿樣區
   * 才變成空白 —— 那次補了入口,沒有補提醒。**做成提醒不是硬擋**:REQ-20260809004
   * 定的是「一張都不勾就是不指定」(當時刻意移除了「不指定」選項),不勾本來就合法。
   *
   * REQ-20260903005 原本做成 inline 琥珀提醒 + 「我確認不需要稿樣」勾選框,使用者
   * 看過成品後回報「表單很累贅」—— 那塊提醒常駐在版面上、又多鎖一顆鈕。改成
   * **按下送出才攔截並跳確認視窗**:沒漏挑的人完全不會遇到它。
   *
   * 唯讀時不攔:那個畫面根本沒有送出鈕。
   */
  const artworkCandidates = artworkImages(interview?.attachments ?? []);
  const artworkEmpty =
    !readOnly && artworkCandidates.length > 0 && artworkAttachmentIds.length === 0;

  // 選項清單與「新增應收單」同一批設定表。**存 id 的那三格(類別 / 憑證 / 收款方式)
  // 要取全部**,再用 `activeOrSelected()` 濾掉停用的:既有單指到的那筆事後被停用時,
  // 只列啟用中的會讓編輯畫面顯示空白,看起來像值不見了。
  // 存文字的那幾格不必如此 —— 舊值會原樣留在已選清單裡(見 `splitItems()`)。
  const { data: categories } = useReferences('product-categories', true);
  const { data: processingOpts } = useReferences('processing-options', false);
  const { data: printColorOpts } = useReferences('print-color-options', false);
  const { data: artworkHandlingOpts } = useReferences('artwork-handling-options', false);
  const { data: shippingMethodOpts } = useReferences('shipping-method-options', false);
  const { data: printSideOpts } = useReferences('print-side-options', false);
  const { data: certs } = useReferences('payment-certs', true);
  const { data: methods } = useReferences('payment-methods', true);
  /** 這個客戶以前用過的私帳對象姓名,給 datalist 當建議值(仍可手動輸入新的) */
  const { data: payerNames } = usePayerNames(customer?.id);
  /** 送貨地址的 placeholder 也拆成兩格 —— 區號進小格,其餘進大格 */
  const shippingPlaceholder = splitAddressZip(customer?.address ?? '留空則沿用客戶主檔地址');
  /** 業務員下拉:在職的使用者(不限角色)。走 accounting 而不是 ADMIN only 的 /users */
  const { data: salesPersons } = useSalesPersons();

  const createMutation = useCreateSalesOrder();
  const updateMutation = useUpdateSalesOrder();
  const isPending = createMutation.isPending || updateMutation.isPending;

  /**
   * 建單模式的單號預覽(REQ-20260813004)。日期一改就重取 —— 單號的年月日看的是
   * **單據日期**不是今天(REQ-20260809005)。編輯模式不打這支:那張單的號已經定了。
   */
  const { data: previewOrderNo } = useNextSalesOrderNo(isEdit ? undefined : txnDate);

  /**
   * 刪除草稿(REQ-20260812001)。二次確認用底部展開的確認列,不用 `window.confirm` ——
   * 理由與清單頁同一條(REQ-20260806005 §三):`confirm` 在 jsdom 測不到,
   * 視覺語言也和其他操作對不上。錯誤沿用下面的 `formError` 區塊顯示,不另開一塊。
   */
  const deleteMutation = useDeleteSalesOrder();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const confirmDelete = (): void => {
    if (!editOrder) return;
    deleteMutation.mutate(editOrder.id, {
      onSuccess: () => onDeleted?.(),
      onError: (err) => setFormError(getApiErrorMessage(err, '刪除失敗')),
    });
  };

  /**
   * 切換帳款對象:切到個人就依名稱預帶「個人 / 不開發票」「現金 / 個人」,
   * 切回公司則三格一起清成 null(= 沿用客戶主檔)。與建單 Modal 完全同一套規則,
   * 判斷邏輯共用 `payer-defaults.ts`,兩個入口不會建出不一樣的私帳單。
   */
  const changePayerType = (next: 'COMPANY' | 'PERSONAL'): void => {
    setPayerType(next);
    if (next === 'COMPANY') {
      setPayerName('');
      setPaymentCertId(null);
      setPaymentMethodId(null);
      return;
    }
    const certId = matchReferenceId(certs?.data, PERSONAL_CERT_KEYWORDS);
    const methodId = matchReferenceId(methods?.data, PERSONAL_METHOD_KEYWORDS);
    if (certId != null) setPaymentCertId(certId);
    if (methodId != null) setPaymentMethodId(methodId);
  };

  /**
   * 合計區的試算(REQ-20260903008)。分兩段是因為稅別:`calcOrderTotals()` 一律當
   * 外加 5% 算,但編輯中的單可能是內含稅 / 免稅(REQ-20260806003)——
   * 直接印它的稅額,收據客戶那種免稅單一進編輯畫面就會自己多出 5%。
   *
   * 第二段與後端 `create()` / `update()` 逐字相同(sales-orders.service.ts):拿未稅合計
   * 當 `gross`,再依稅別重拆。稅別自 REQ-20260921002 起**是表單上那顆下拉**
   * (在此之前只讀 `editOrder.taxMode`,編輯改不動),所以改一下下拉,
   * 右邊三個數字立刻跟著變 —— 這正是把它擺在合計區旁邊的理由。
   *
   * 稅率分兩種情況取:
   *  - 稅別**沒被動過**(還是單據原本那個)→ 用單據存的 `taxRate`,讓畫面忠實反映
   *    這張單存的是什麼。REQ-20260903008 §5.2 那 3 張「外加但稅率 0」的舊單靠這條
   *    才會顯示稅額 0 而不是憑空多出 5%。
   *  - 使用者**改過稅別** → 傳 null(= 後端預設 5%)。表單不送 `taxRate`,
   *    所以存檔時套的一定是後端預設值;繼續顯示舊稅率會讓畫面與存檔分家。
   * `?? null` 而不是留 undefined:`exactOptionalPropertyTypes` 不收顯式的 undefined,
   * 而 `calcTaxModeAmounts()` 對 null 與沒帶一樣是預設 5%。
   */
  const totals = useMemo(() => {
    const base = calcOrderTotals({ lines: lines.map(linePricing), plateFee, dieFee });
    return {
      lineSubtotal: base.lineSubtotal,
      ...calcTaxModeAmounts({
        gross: base.subtotal,
        taxMode,
        taxRate: taxMode === editOrder?.taxMode ? (editOrder?.taxRate ?? null) : null,
      }),
    };
  }, [lines, plateFee, dieFee, taxMode, editOrder?.taxMode, editOrder?.taxRate]);

  const setLine = (index: number, patch: Partial<LineDraft>): void => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  /** 切換這一列的計價方式:另一格清成空字串,兩格同時有值的狀態不存在 */
  const changePriceMode = (index: number, mode: 'unit' | 'total'): void => {
    setLine(index, mode === 'unit' ? { priceMode: mode, totalPrice: '' } : { priceMode: mode, unitPrice: '' });
  };

  const submitOrder = (): void => {
    setFormError(null);
    if (!customer) {
      setFormError('請先選擇客戶');
      return;
    }

    const input: CreateSalesOrderInput = {
      customerId: customer.id,
      interviewId,
      salesPersonId,
      txnDate,
      dueDate: null,
      shippingAddress: shippingAddress || customer.address || null,
      // 公司公帳:姓名 / 憑證 / 收款方式一律送 null(= 沿用客戶主檔)。切到個人再切回來時,
      // 那幾格已經不在畫面上,`changePayerType()` 也清過了,這裡是最後一道保險。
      payerType,
      payerName: payerType === 'PERSONAL' ? payerName || null : null,
      paymentCertId: payerType === 'PERSONAL' ? paymentCertId : null,
      paymentMethodId: payerType === 'PERSONAL' ? paymentMethodId : null,
      printColor: joinItems(printColorItems),
      artworkHandling: joinItems(artworkHandlingItems),
      shippingMethod: shippingMethod || null,
      printSides: printSides || null,
      artworkAttachmentIds,
      plateFee: plateFee || null,
      dieFee: dieFee || null,
      // 稅別(REQ-20260921002)。**不送 `taxRate`** —— 維持既有行為:後端對免稅一律
      // 存 0,其餘沒帶就套預設 5%。建單與編輯共用這個 input,所以只加一次。
      taxMode,
      notes: notes || null,
      lines: lines.map((l) => ({
        productId: l.productId,
        productCode: l.productCode,
        productName: l.productName,
        productSpec: l.productSpec || null,
        productMaterial: l.productMaterial || null,
        productCategoryId: l.productCategoryId,
        productProcessing: l.productProcessing || null,
        unitName: l.unitName || null,
        qty: l.qty,
        // 擇一送出:填總價時**不送單價**,讓後端拿總價當金額的權威來源
        // (前端自己除一次的話,1000 ÷ 3 = 333.33 乘回去就少一分錢)
        unitPrice: l.priceMode === 'unit' ? l.unitPrice : undefined,
        totalPrice: l.priceMode === 'total' ? l.totalPrice : undefined,
        sizeUnits: l.sizeUnits || null,
        moldCount: l.moldCount || null,
      })),
    } as CreateSalesOrderInput;

    if (editOrder) {
      // 更新沒有 customerId(客戶不可換),schema 也是 omit 掉它的那一份
      const { customerId: _customerId, ...rest } = input;
      const parsedUpdate = UpdateSalesOrderSchema.safeParse(rest);
      if (!parsedUpdate.success) {
        setFormError(describeZodError(parsedUpdate.error, FIELD_LABELS));
        return;
      }
      updateMutation.mutate(
        { id: editOrder.id, ...parsedUpdate.data },
        {
          onSuccess: (order) => onCreated({ id: order.id, orderNo: order.orderNo }),
          onError: (err) => setFormError(getApiErrorMessage(err, '儲存失敗', FIELD_LABELS)),
        },
      );
      return;
    }

    const parsed = CreateSalesOrderSchema.safeParse(input);
    if (!parsed.success) {
      setFormError(describeZodError(parsed.error, FIELD_LABELS));
      return;
    }

    createMutation.mutate(parsed.data, {
      onSuccess: (order) => onCreated({ id: order.id, orderNo: order.orderNo }),
      onError: (err) => setFormError(getApiErrorMessage(err, '建立失敗', FIELD_LABELS)),
    });
  };

  /**
   * 送出的守門(REQ-20260903009)。掛在 form 的送出路徑上,所以按鈕點擊與 Enter 都吃得到。
   *
   * **刻意不記「已經確認過」** —— 確認視窗的「直接儲存」是一次性地直接呼叫
   * `submitOrder()`,不是先解鎖再送出。留一格持久 state 的話,「確認過 → 又把圖
   * 全部取消勾選 → 再送出」會靜默放行,那正是 REQ-20260903005 §三 特地釘住的行為。
   */
  const onSubmit = (): void => {
    if (artworkEmpty) {
      setArtworkConfirmOpen(true);
      return;
    }
    submitOrder();
  };

  return (
    <form
      className="grid gap-4 bg-white shadow-sm rounded-md p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {/* 這家客戶「下次要注意的事」(REQ-20260918002)。唯讀、可收合、**不擋送出**。
          刻意留在下面那個 `fieldset[disabled]` **外面**:唯讀檢視也要看得到這條提醒,
          收在 fieldset 裡面的話連收合鈕都會被一起停用(理由同「取消 / 關閉」那兩顆)。
          還沒選到客戶時 `ownerId` 是 undefined —— 元件不打 API 也不顯示。 */}
      <OpenRemindersBanner ownerType="customer" ownerId={customer?.id} />
      {/*
        唯讀模式的唯一開關(REQ-20260824009)。`fieldset[disabled]` 會連帶停用所有後代的
        input / select / textarea / button —— 包含 `PartyPicker`、`MultiSelectField`、
        `ArtworkPicker`、`InterviewImportPicker` 這幾個自己沒有 `disabled` prop 的共用元件,
        所以不必為了唯讀去改別的畫面也在用的元件。
        **刻意不逐格傳 `disabled`**:這張表單三十幾格 + 五個子元件,逐格傳必有遺漏,
        而遺漏的症狀是靜默的(唯讀模式下某一格改得動)。原生 `readOnly` 也不夠 ——
        它對 `<select>` / checkbox / radio 無效,那正是這裡最多的欄位型別。
        「取消 / 關閉」與錯誤訊息留在 fieldset **外面**,不然使用者離不開畫面。
      */}
      <fieldset disabled={readOnly} className="grid gap-4">
        {/*
          單號放最上面(REQ-20260811005)。編輯模式直接顯示這張單的號;建單模式在成功前
          顯示**預覽值**(REQ-20260813004)—— 使用者要求一打開就看得到號、而且跟著
          訂單日期變。

          預覽值是跟後端要的(`useNextSalesOrderNo`),**不是前端自己算 seq**:
          REQ-20260809002 §五 否掉的是「前端重算一份 nextOrderNo」,那會跟後端邏輯分岔。
          但它仍**只是預覽** —— 序號在寫入當下才決定、撞號還會重算重試(CLAUDE.md §6.3),
          所以旁邊固定掛一句「預覽,實際以儲存後為準」,不讓使用者以為這是保證值。
          建立成功後換成後端回的**正式**號碼(不是拿預覽值頂替)。
        */}
        <div
          className="flex items-baseline gap-2 border-b border-slate-200 pb-2 text-sm"
          data-testid="so-order-no"
        >
          <span className="text-slate-500">單號</span>
          {editOrder ? (
            <strong className="font-mono text-slate-900">{editOrder.orderNo}</strong>
          ) : createMutation.isSuccess ? (
            <strong className="font-mono text-slate-900">{createMutation.data.orderNo}</strong>
          ) : (
            <>
              <strong className="font-mono text-slate-900">{previewOrderNo ?? '—'}</strong>
              <span className="text-xs text-slate-400">預覽,實際以儲存後為準</span>
            </>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <PartyPicker type="customer" value={customer} onChange={setCustomer} disabled={isEdit} />

          <div className="grid gap-1">
            <label className="text-sm text-slate-600" htmlFor="so-txn-date">
              訂單日期
            </label>
            <input
              id="so-txn-date"
              type="date"
              value={txnDate}
              onChange={(e) => setTxnDate(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/*
            業務員(REQ-20260815002)。留白 = 未指定,與歷史單的 null 是同一個值 ——
            不預設成「目前登入者」:補開別人的單是常態,猜錯比留白難發現。
          */}
          <div className="grid gap-1">
            <label className="text-sm text-slate-600" htmlFor="so-sales-person">
              業務員
            </label>
            <select
              id="so-sales-person"
              data-testid="so-sales-person"
              value={salesPersonId ?? ''}
              onChange={(e) => setSalesPersonId(e.target.value === '' ? null : Number(e.target.value))}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">未指定</option>
              {(salesPersons?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {customer ? (
          <InterviewImportPicker
            customerId={customer.id}
            selectedInterviewId={interviewId}
            onSelect={changeInterview}
            onApplySpec={(spec) => {
              // 導入的規格文字先填進第一列還空著的欄位,不覆蓋已經打好的內容
              setLines((prev) => {
                const first = prev[0] ?? { ...EMPTY_LINE };
                const patched: LineDraft = first.productName
                  ? { ...first, productSpec: first.productSpec || spec }
                  : { ...first, productName: spec };
                return [patched, ...prev.slice(1)];
              });
            }}
            onApplyNotes={(text) => setNotes((prev) => (prev ? `${prev}\n${text}` : text))}
          />
        ) : null}

        <div className="grid gap-1">
          {/* 兩個 input 各自帶 aria-label,所以標題不再是 `<label htmlFor>` */}
          <span className="text-sm text-slate-600">送貨地址</span>
          <AddressInput
            label="送貨地址"
            value={shippingAddress}
            onChange={setShippingAddress}
            zipPlaceholder={shippingPlaceholder.zip || undefined}
            placeholder={shippingPlaceholder.rest}
            inputClassName="border rounded-md px-3 py-2 text-sm"
          />
        </div>

        {/*
          帳款抬頭 / 對象(REQ-20260810002)。決定這張單進不進公司月結對帳單,
          所以放在品項金額前面 —— 與「新增應收單」的順序一致。
        */}
        <fieldset className="grid gap-2 border rounded-md p-3" data-testid="so-payer">
          <legend className="text-sm font-medium text-slate-700">帳款抬頭 / 對象</legend>
          <div className="flex gap-4 text-sm text-slate-700">
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="so-payer-type"
                checked={payerType === 'COMPANY'}
                onChange={() => changePayerType('COMPANY')}
                data-testid="so-payer-type-company"
              />
              公司公帳
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="so-payer-type"
                checked={payerType === 'PERSONAL'}
                onChange={() => changePayerType('PERSONAL')}
                data-testid="so-payer-type-personal"
              />
              個人(私帳)
            </label>
          </div>

          {payerType === 'COMPANY' ? (
            /* 公司公帳沿用客戶主檔,不重複輸入 —— 顯示帶出來的值讓使用者核對 */
            <div className="text-sm text-slate-500" data-testid="so-payer-company-hint">
              沿用客戶主檔:{customer?.simpleName ?? '—'}
              (憑證 {customer?.paymentCert ?? '—'} / 收款方式 {customer?.paymentMethod ?? '—'})
            </div>
          ) : (
            <div className="grid gap-2 md:grid-cols-3">
              <div className="grid gap-1">
                <label className="text-xs text-slate-600" htmlFor="so-payer-name">
                  實際收款對象
                </label>
                <input
                  id="so-payer-name"
                  type="text"
                  list="so-payer-name-options"
                  value={payerName}
                  onChange={(e) => setPayerName(e.target.value)}
                  placeholder="例 黃勝彬(可直接輸入,不必先建檔)"
                  className="border rounded-md px-2 py-1.5 text-sm"
                  data-testid="so-payer-name"
                />
                {/* 這個客戶用過的姓名當建議值;沿用單位欄那套 input + datalist,不新開一張表 */}
                <datalist id="so-payer-name-options">
                  {(payerNames ?? []).map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
              </div>

              <ReferenceSelect
                label="收執憑證"
                testId="so-payment-cert"
                emptyLabel="沿用客戶主檔"
                options={activeOrSelected(certs?.data, paymentCertId)}
                value={paymentCertId}
                onChange={setPaymentCertId}
              />
              <ReferenceSelect
                label="收款方式"
                testId="so-payment-method"
                emptyLabel="沿用客戶主檔"
                options={activeOrSelected(methods?.data, paymentMethodId)}
                value={paymentMethodId}
                onChange={setPaymentMethodId}
              />
            </div>
          )}
        </fieldset>

        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium text-slate-700">品項</legend>
          {lines.map((line, i) => (
            <div key={i} className="grid gap-2 border rounded-md p-3 md:grid-cols-4">
              <ReferenceSelect
                label="貨品類別"
                testId={`so-line-category-${i}`}
                emptyLabel="—"
                options={activeOrSelected(categories?.data, line.productCategoryId)}
                value={line.productCategoryId}
                onChange={(v) => setLine(i, { productCategoryId: v })}
              />
              <LineField label="品名" value={line.productName} onChange={(v) => setLine(i, { productName: v })} />
              <LineField label="規格" value={line.productSpec} onChange={(v) => setLine(i, { productSpec: v })} />
              <LineField label="材質" value={line.productMaterial} onChange={(v) => setLine(i, { productMaterial: v })} />
              {/*
                加工與印刷顏色 / 稿件處理同一種填法(REQ-20260819008):常用選項打勾 + 可手動輸入,
                存的仍是「、」串接的文字,只是這一格是逐品項各存各的。
                佔滿整列 —— 擠在四欄格線的一格裡 checkbox 排不開。
              */}
              <div className="md:col-span-4">
                <MultiSelectField
                  label="加工"
                  testId={`so-line-processing-${i}`}
                  options={optionNames(processingOpts?.data)}
                  selected={splitItems(line.productProcessing)}
                  onChange={(items) => setLine(i, { productProcessing: joinItems(items) ?? '' })}
                />
              </div>
              {/*
                報價談的是單價還是整批總價,兩種都有(REQ-20260905007)——
                與訪談「新增應收單」同一組切換,只是這裡逐品項各選各的。
                `name` 帶上品項 index,不然多個品項的 radio 會被瀏覽器當成同一組互相搶。
              */}
              <div className="md:col-span-4 flex gap-4 text-sm text-slate-700">
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={`so-line-price-mode-${i}`}
                    checked={line.priceMode === 'unit'}
                    onChange={() => changePriceMode(i, 'unit')}
                    data-testid={`so-line-${i}-price-mode-unit`}
                  />
                  填單價
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={`so-line-price-mode-${i}`}
                    checked={line.priceMode === 'total'}
                    onChange={() => changePriceMode(i, 'total')}
                    data-testid={`so-line-${i}-price-mode-total`}
                  />
                  填總價
                </label>
              </div>

              <LineField label="單位" value={line.unitName} onChange={(v) => setLine(i, { unitName: v })} />
              <LineField label="數量" value={line.qty} onChange={(v) => setLine(i, { qty: v })} inputMode="decimal" />
              {line.priceMode === 'unit' ? (
                <LineField label="單價" value={line.unitPrice} onChange={(v) => setLine(i, { unitPrice: v })} inputMode="decimal" />
              ) : (
                <LineField
                  label="總價"
                  value={line.totalPrice}
                  onChange={(v) => setLine(i, { totalPrice: v })}
                  inputMode="decimal"
                  placeholder="例 1000"
                />
              )}
              <LineField
                label="才數"
                value={line.sizeUnits}
                onChange={(v) => setLine(i, { sizeUnits: v })}
                inputMode="decimal"
                hint="填了才數就改用「才數 × 模數 × 單價」計價"
              />
              <LineField label="模數" value={line.moldCount} onChange={(v) => setLine(i, { moldCount: v })} inputMode="decimal" />

              <div className="md:col-span-4 flex items-center justify-between text-sm">
                <span className="text-slate-500">
                  小計{' '}
                  <span className="font-mono text-slate-900">{calcLineAmount(linePricing(line))}</span>
                </span>
                {/* 唯讀時整個收起來,不是反灰 —— 反灰的按鈕看起來還是「可以按的東西」 */}
                {!readOnly && lines.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                    className="text-red-600 hover:underline text-xs"
                  >
                    刪除此列
                  </button>
                ) : null}
              </div>
            </div>
          ))}
          {readOnly ? null : (
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
              className="justify-self-start text-blue-600 hover:underline text-sm"
            >
              + 新增品項
            </button>
          )}
        </fieldset>

        {/*
          藝品印刷訂單的現場欄位(REQ-20260809002、REQ-20260810003)。單頭欄位 ——
          一張單只有一種印刷顏色 / 出貨方式 / 面別,所以不放在品項裡。
          印刷顏色 / 稿件處理可複選;出貨方式 / 印刷面別維持單選(需求明確要求),
          與「新增應收單」逐格對應。出貨方式的單選改用 `MultiSelectField multiple={false}`
          (REQ-20260908003),印刷面別仍是原生 `<input list>` + `<datalist>`。
        */}
        <fieldset className="grid gap-3 border rounded-md p-3" data-testid="so-print-fields">
          <legend className="text-sm font-medium text-slate-700">印刷 / 出貨</legend>
          <MultiSelectField
            label="印刷顏色"
            testId="so-print-color"
            options={optionNames(printColorOpts?.data)}
            selected={printColorItems}
            onChange={setPrintColorItems}
          />
          <MultiSelectField
            label="稿件處理"
            testId="so-artwork-handling"
            options={optionNames(artworkHandlingOpts?.data)}
            selected={artworkHandlingItems}
            onChange={setArtworkHandlingItems}
          />
          {/* `items-start`:出貨方式展開選項時這一列會變高,不加的話旁邊的印刷面別
              會被拉成一個大方框(REQ-20260821007 §三 在進貨單踩過同一件事) */}
          <div className="grid items-start gap-2 md:grid-cols-2">
            <DatalistField
              label="印刷面別"
              testId="so-print-sides"
              value={printSides}
              onChange={setPrintSides}
              options={optionNames(printSideOpts?.data)}
              placeholder="例 單面 / 雙面(可手動輸入)"
            />
            {/* 出貨方式仍是單選,但改用 `MultiSelectField multiple={false}`
                (REQ-20260908003)—— datalist 會拿現值過濾建議清單,選過之後
                按 ▼ 等於沒反應。與「新增應收單」那一格逐格對應(REQ-20260811003)。
                ⚠️ **不能用 `splitItems()`**:正式環境可能有含「、」的舊值,拆開之後
                單選只留得住一顆 = 靜默改資料(REQ-20260821007 §二)。整串當一顆顯示、
                使用者不動它就原樣送回去 */}
            <MultiSelectField
              label="出貨方式"
              testId="so-shipping-method"
              multiple={false}
              options={optionNames(shippingMethodOpts?.data)}
              selected={shippingMethod ? [shippingMethod] : []}
              onChange={(items) => setShippingMethod(items[0] ?? '')}
            />
          </div>
        </fieldset>

        {/*
          商品稿樣(REQ-20260811004)。候選圖片來自上面關聯的那筆訪談 ——
          沒關聯訪談、或那筆訪談沒有圖片附件時,`ArtworkPicker` 自己回 null 整塊不顯示,
          與「新增應收單」同一套規則(同一個元件)。
          包一層 div 是為了「返回勾選」時捲回這裡並聚焦第一格(REQ-20260903009)。
        */}
        <div ref={artworkSectionRef}>
          <ArtworkPicker
            attachments={interview?.attachments ?? []}
            selected={artworkAttachmentIds}
            onChange={setArtworkAttachmentIds}
            testId="so-artwork"
          />
        </div>

        <div className="grid gap-2 md:grid-cols-2">
          <LineField label="版費" value={plateFee} onChange={setPlateFee} inputMode="decimal" />
          <LineField label="刀模費" value={dieFee} onChange={setDieFee} inputMode="decimal" />
        </div>

        <div className="grid gap-1">
          <label className="text-sm text-slate-600" htmlFor="so-notes">
            備註
          </label>
          <textarea
            id="so-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
          />
        </div>

        {/*
          單頭稅別(REQ-20260921002)。使用者找不到地方改稅額 —— 稅額是稅別算出來的,
          而這張表單一直沒有稅別欄位。擺法比照進貨單(REQ-20260901010):
          改成免稅時稅額就是 0,下拉貼著合計區,改完立刻對得起來。
          選項文字與訪談頁「新增應收單」逐字一致,不共用常數(兩邊都只有三個詞)。
          留在 `fieldset[disabled]` 裡面 —— 唯讀檢視因此自動改不動。
        */}
        <div className="grid gap-1 md:w-72 md:justify-self-end">
          <label className="text-sm text-slate-600" htmlFor="so-tax-mode">
            稅別
          </label>
          <select
            id="so-tax-mode"
            data-testid="so-tax-mode"
            value={taxMode}
            onChange={(e) => setTaxMode(e.target.value as SalesOrderDetail['taxMode'])}
            className="border rounded-md px-3 py-2 text-sm"
          >
            <option value="EXCLUSIVE">外加 5%</option>
            <option value="INCLUSIVE">內含</option>
            <option value="EXEMPT">免稅</option>
          </select>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm border-t pt-3 md:w-72 md:justify-self-end">
          <dt className="text-slate-500">品項小計</dt>
          <dd className="text-right font-mono">{formatMoney(totals.lineSubtotal)}</dd>
          <dt className="text-slate-500">未稅合計</dt>
          <dd className="text-right font-mono">{formatMoney(totals.subtotal)}</dd>
          <dt className="text-slate-500">稅額{TAX_MODE_NOTE[taxMode]}</dt>
          <dd className="text-right font-mono">{formatMoney(totals.tax)}</dd>
          <dt className="font-medium text-slate-700">含稅總計</dt>
          <dd className="text-right font-mono font-medium">{formatMoney(totals.total)}</dd>
        </dl>
      </fieldset>

      {formError ? (
        <div role="alert" className="text-red-600 text-sm">
          {formError}
        </div>
      ) : null}

      {/* 刪除的二次確認列。樣式沿用清單頁那條(琥珀底 + 紅字「確定刪除」) */}
      {showDeleteConfirm && editOrder ? (
        <div
          className="flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm"
          data-testid="so-delete-confirm"
        >
          <span className="mr-auto text-slate-700">
            確定要刪除草稿 {editOrder.orderNo}?刪除後無法復原。
          </span>
          <button
            type="button"
            disabled={deleteMutation.isPending}
            onClick={confirmDelete}
            className="text-red-600 hover:underline disabled:opacity-50"
          >
            確定刪除
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(false)}
            className="text-slate-600 hover:underline"
          >
            取消
          </button>
        </div>
      ) : null}

      <div className="flex gap-2 justify-end items-center">
        {/*
          只有草稿給刪(REQ-20260812001)。非草稿不顯示、也不解釋為什麼 ——
          與清單頁一致;已過帳的單要收回請走「作廢」。
          擺在最左邊(`mr-auto`)離送出鈕遠一點,樣式沿用庫存紀錄那顆刪除鈕。
        */}
        {editOrder?.status === 'DRAFT' && !readOnly ? (
          <button
            type="button"
            onClick={() => {
              setFormError(null);
              setShowDeleteConfirm(true);
            }}
            className="mr-auto text-sm border rounded px-3 py-1.5 text-red-600 border-red-300 hover:bg-red-50"
            data-testid="so-delete-btn"
          >
            刪除
          </button>
        ) : null}
        {/* 未儲存的小標記(REQ-20260917002),樣式沿用廠商操作台標題旁那一顆 */}
        {dirty ? (
          <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5 shrink-0">
            未儲存
          </span>
        ) : null}
        {/* 這顆**永遠**在,而且在 fieldset 外面 —— 唯讀時它是使用者離開這個畫面的唯一出口。
            有未儲存變更時先問一次再離開(REQ-20260917002);唯讀時改不動任何一格,
            dirty 恆為 false → 按下去就直接關掉,與改動前逐字相同 */}
        <button
          type="button"
          onClick={handleCancel}
          className="px-3 py-1.5 rounded border text-slate-700 hover:bg-slate-50"
        >
          {readOnly ? '關閉' : '取消'}
        </button>
        {/* 唯讀時不顯示送出鈕:這張單根本不接受寫入,留一顆反灰的按鈕只會讓人一直去試 */}
        {readOnly ? null : (
          /* 編輯模式的「儲存修改」依 dirty 變色(REQ-20260917002)—— 沒改過時長得和
             左邊的「取消」一樣,一動欄位就變藍底白字。灰框那組就是取消鍵的 class 加
             `disabled:opacity-50`;**padding 保持 `px-3 py-1.5` 不動**,不然切換 dirty
             時整排按鈕會抖一下(REQ-20260819011 §二取捨 1)。
             建單模式(「建立草稿」)一律藍色(REQ-20260825009 的決定)。 */
          <button
            type="submit"
            disabled={isPending}
            className={
              isEdit && !dirty
                ? 'px-3 py-1.5 rounded border text-slate-700 hover:bg-slate-50 disabled:opacity-50'
                : 'bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50'
            }
          >
            {isEdit
              ? isPending
                ? '儲存中...'
                : '儲存修改'
              : isPending
                ? '建立中...'
                : '建立草稿'}
          </button>
        )}
      </div>

      {/*
        有圖可挑卻一張都沒勾時的送出確認(REQ-20260903009)。在 fieldset 外面 ——
        唯讀模式下 `artworkEmpty` 恆為 false,本來就開不起來,但放外面才不會被
        `fieldset[disabled]` 連帶停用裡面的兩顆鈕。
      */}
      <ConfirmDialog
        open={artworkConfirmOpen}
        title="尚未勾選商品稿樣"
        message="系統偵測到您已上傳商品稿樣,但尚未勾選帶入訂單。請確認是否要直接儲存?"
        confirmLabel="直接儲存"
        cancelLabel="返回勾選"
        tone="primary"
        onConfirm={() => submitOrder()}
        onClose={() => setArtworkConfirmOpen(false)}
        onCancel={() => {
          // jsdom 沒有實作 scrollIntoView,直接呼叫會在測試裡丟 TypeError
          artworkSectionRef.current?.scrollIntoView?.({ block: 'center' });
          artworkSectionRef.current
            ?.querySelector<HTMLInputElement>('input[type="checkbox"]')
            ?.focus();
        }}
      />

      {/* 有未儲存變更時按「取消」的攔截(REQ-20260917002)。文案逐字沿用客戶 /
          廠商操作台(REQ-20260916004),同一件事不寫成兩種說法。
          同樣在 fieldset 外面 —— 唯讀時它本來就開不起來,但放外面才不會被
          `fieldset[disabled]` 連帶停用裡面的兩顆鈕 */}
      <ConfirmDialog
        open={cancelConfirm}
        title="放棄未儲存的變更?"
        message="剛剛輸入但還沒儲存的內容會全部消失,已經存進系統的資料不受影響。"
        confirmLabel="放棄變更"
        cancelLabel="繼續編輯"
        tone="danger"
        onConfirm={onCancel}
        onClose={() => setCancelConfirm(false)}
      />
    </form>
  );
}

function LineField({
  label,
  value,
  onChange,
  inputMode,
  hint,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  inputMode?: 'decimal';
  hint?: string;
  placeholder?: string;
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <label className="text-xs text-slate-600">
        {label}
        <input
          type="text"
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-0.5 w-full border rounded-md px-2 py-1.5 text-sm"
        />
      </label>
      {hint ? <span className="text-[11px] text-slate-400">{hint}</span> : null}
    </div>
  );
}

/** 存 id 的下拉(貨品類別 / 收執憑證 / 收款方式)。空字串 = 沒選 = null */
function ReferenceSelect({
  label,
  testId,
  emptyLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  testId: string;
  emptyLabel: string;
  options: { id: number; name: string }[];
  value: number | null;
  onChange: (v: number | null) => void;
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <label className="text-xs text-slate-600">
        {label}
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="mt-0.5 w-full border rounded-md px-2 py-1.5 text-sm"
          data-testid={testId}
        >
          <option value="">{emptyLabel}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/** 常用選項只是建議、仍可手動輸入的單選文字欄位(印刷面別) */
function DatalistField({
  label,
  testId,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  testId: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}): JSX.Element {
  const listId = `${testId}-options`;
  return (
    <div className="grid gap-1">
      <label className="text-xs text-slate-600">
        {label}
        <input
          type="text"
          list={listId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-0.5 w-full border rounded-md px-2 py-1.5 text-sm"
          data-testid={testId}
        />
      </label>
      <datalist id={listId}>
        {options.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
    </div>
  );
}

/**
 * 下拉只列啟用中的選項,**但目前這張單指到的那筆一定留著** ——
 * 設定頁事後把某個類別 / 憑證停用時,舊單的編輯畫面不該顯示成空白
 * (單據是歷史憑證,值還在 DB 裡,看不到只會讓人以為資料掉了)。
 */
function activeOrSelected(
  rows: { id: number; name: string; isActive: boolean }[] | undefined,
  selectedId: number | null,
): { id: number; name: string }[] {
  return (rows ?? []).filter((r) => r.isActive || r.id === selectedId);
}

/** 明細列 → 表單草稿列(編輯模式的初始值) */
function toLineDraft(l: SalesOrderDetail['lines'][number]): LineDraft {
  /*
   * 這一列原本是用「填總價」建的嗎(REQ-20260905007)?
   * 判斷方式:拿單據存的單價重算一次金額,對不上就是總價模式 ——
   * 總價模式下單價是回推的參考值(1000 ÷ 3 = 333.33),乘回去只有 999.99。
   *
   * **不做這個偵測的話,使用者只是打開修改頁按一次儲存,單據金額就會從 1000
   * 悄悄變成 999.99** —— `update()` 是整批刪掉重建明細(REQ-20260811003),
   * 帶成單價模式送出去,後端就會照 3 × 333.33 重算。沒有任何訊息,對帳時才發現。
   */
  const isTotalMode =
    Number(
      calcLineAmount({
        qty: l.qty,
        unitPrice: l.unitPrice,
        sizeUnits: l.sizeUnits,
        moldCount: l.moldCount,
        discount: l.discount,
      }),
    ) !== Number(l.amount);
  return {
    productName: l.productName,
    productSpec: l.productSpec ?? '',
    productMaterial: l.productMaterial ?? '',
    productCategoryId: l.productCategoryId,
    productProcessing: l.productProcessing ?? '',
    unitName: l.unitName ?? '',
    qty: l.qty,
    priceMode: isTotalMode ? 'total' : 'unit',
    unitPrice: l.unitPrice,
    totalPrice: isTotalMode ? l.amount : '',
    sizeUnits: l.sizeUnits ?? '',
    moldCount: l.moldCount ?? '',
    productId: l.productId,
    productCode: l.productCode,
  };
}

/**
 * 編輯模式下 PartyPicker 只是唯讀顯示(客戶不能換),所以用單據上已有的客戶欄位
 * 湊出 PartyInfo 就夠,不必再打一次 lookup API。查不到的欄位留 null,顯示成「—」。
 */
function partyFromOrder(order: SalesOrderDetail): PartyInfo {
  return {
    id: order.customerId,
    type: 'customer',
    code: order.customerCode,
    fullName: order.customerFullName,
    simpleName: order.customerSimpleName,
    taxId: null,
    address: order.customerAddress,
    addressInvoice: null,
    contact: null,
    phone: null,
    phones: [],
    email: null,
    settlementCycle: null,
    settlementCycleLabel: null,
    settlementDay: null,
    paymentCert: order.customerPaymentCert,
    paymentMethod: order.customerPaymentMethod,
  };
}

/** 訂單日期預設今天。容器 / 瀏覽器時區不一定是台北,明確指定。 */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
